// src/components/controlPanel/CalendarGrid.jsx
import { useState, useMemo } from "react";
import CalendarDayCell from "./CalendarDayCell";

const DAYS_OF_WEEK = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

export default function CalendarGrid({
  defaultOpeningHours,
  overrides,
  selectedDates,
  onSelectDate,
  onSelectRange,
}) {
  const [viewDate, setViewDate] = useState(new Date());
  const [rangeStart, setRangeStart] = useState(null);

  const today = useMemo(() => {
    const t = new Date();
    t.setHours(0, 0, 0, 0);
    return t;
  }, []);

  const maxDate = useMemo(() => {
    const m = new Date(today);
    m.setFullYear(m.getFullYear() + 1);
    return m;
  }, [today]);

  const { year, month, calendarDays } = useMemo(() => {
    const y = viewDate.getFullYear();
    const m = viewDate.getMonth();

    // First day of month
    const firstDay = new Date(y, m, 1);
    // Last day of month
    const lastDay = new Date(y, m + 1, 0);

    // Start from Monday of the week containing the first day
    const startDayOfWeek = (firstDay.getDay() + 6) % 7; // Convert Sun=0 to Mon=0
    const startDate = new Date(firstDay);
    startDate.setDate(startDate.getDate() - startDayOfWeek);

    const days = [];
    const current = new Date(startDate);

    // Generate 6 weeks (42 days) to ensure we cover all cases
    for (let i = 0; i < 42; i++) {
      days.push(new Date(current));
      current.setDate(current.getDate() + 1);
    }

    return { year: y, month: m, calendarDays: days };
  }, [viewDate]);

  const monthName = new Date(year, month).toLocaleDateString("en-US", { month: "long", year: "numeric" });

  const canGoPrev = useMemo(() => {
    const prevMonth = new Date(year, month - 1, 1);
    return prevMonth >= new Date(today.getFullYear(), today.getMonth(), 1);
  }, [year, month, today]);

  const canGoNext = useMemo(() => {
    const nextMonth = new Date(year, month + 1, 1);
    return nextMonth <= maxDate;
  }, [year, month, maxDate]);

  const goToPrevMonth = () => {
    if (canGoPrev) {
      setViewDate(new Date(year, month - 1, 1));
    }
  };

  const goToNextMonth = () => {
    if (canGoNext) {
      setViewDate(new Date(year, month + 1, 1));
    }
  };

  const getDefaultHoursForDate = (date) => {
    const dayNames = ["sunday", "monday", "tuesday", "wednesday", "thursday", "friday", "saturday"];
    const dayName = dayNames[date.getDay()];
    return defaultOpeningHours?.[dayName] || null;
  };

  const formatDateLocal = (date) => {
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, "0");
    const d = String(date.getDate()).padStart(2, "0");
    return `${y}-${m}-${d}`;
  };

  const getOverrideForDate = (date) => {
    const dateStr = formatDateLocal(date);
    return overrides.find((o) => o.date === dateStr) || null;
  };

  const isDateSelected = (date) => {
    const dateStr = formatDateLocal(date);
    return selectedDates.includes(dateStr);
  };

  const handleSelectDate = (date) => {
    if (date < today) return; // Can't select past dates
    setRangeStart(date);
    onSelectDate(date);
  };

  const handleShiftSelect = (date) => {
    if (date < today) return;
    if (rangeStart && rangeStart !== date) {
      const start = rangeStart < date ? rangeStart : date;
      const end = rangeStart < date ? date : rangeStart;
      onSelectRange(start, end);
    }
    setRangeStart(null);
  };

  return (
    <div className="w-full">
      {/* Month navigation */}
      <div className="flex items-center justify-between mb-4">
        <button
          type="button"
          onClick={goToPrevMonth}
          disabled={!canGoPrev}
          className={`p-2 rounded-lg transition flex-shrink-0 ${
            canGoPrev ? "hover:bg-gray-100 text-gray-700" : "text-gray-300 cursor-not-allowed"
          }`}
        >
          <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 19l-7-7 7-7" />
          </svg>
        </button>
        <h3 className="text-sm font-semibold text-gray-900 whitespace-nowrap">{monthName}</h3>
        <button
          type="button"
          onClick={goToNextMonth}
          disabled={!canGoNext}
          className={`p-2 rounded-lg transition ${
            canGoNext ? "hover:bg-gray-100 text-gray-700" : "text-gray-300 cursor-not-allowed"
          }`}
        >
          <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7" />
          </svg>
        </button>
      </div>

      {/* Day of week headers */}
      <div className="grid grid-cols-7 gap-1 mb-1">
        {DAYS_OF_WEEK.map((day) => (
          <div key={day} className="text-center text-xs font-medium text-gray-500 py-1">
            {day}
          </div>
        ))}
      </div>

      {/* Calendar grid */}
      <div className="grid grid-cols-7 gap-1">
        {calendarDays.map((date, idx) => (
          <CalendarDayCell
            key={idx}
            date={date}
            defaultHours={getDefaultHoursForDate(date)}
            override={getOverrideForDate(date)}
            isSelected={isDateSelected(date)}
            isToday={date.getTime() === today.getTime()}
            isCurrentMonth={date.getMonth() === month}
            onSelect={handleSelectDate}
            onShiftSelect={handleShiftSelect}
          />
        ))}
      </div>

      {/* Legend */}
      <div className="grid grid-cols-2 sm:flex sm:flex-wrap gap-2 sm:gap-4 mt-4 text-xs text-gray-600">
        <div className="flex items-center gap-1.5">
          <span className="w-3 h-3 rounded bg-green-50 border border-green-200 flex-shrink-0" />
          <span>Open (default)</span>
        </div>
        <div className="flex items-center gap-1.5">
          <span className="w-3 h-3 rounded bg-gray-100 border border-gray-200 flex-shrink-0" />
          <span>Closed (default)</span>
        </div>
        <div className="flex items-center gap-1.5">
          <span className="w-3 h-3 rounded bg-teal-100 border border-teal-200 relative flex-shrink-0">
            <span className="absolute -top-0.5 -right-0.5 w-1.5 h-1.5 bg-teal-500 rounded-full" />
          </span>
          <span>Override (open)</span>
        </div>
        <div className="flex items-center gap-1.5">
          <span className="w-3 h-3 rounded bg-orange-100 border border-orange-200 relative flex-shrink-0">
            <span className="absolute -top-0.5 -right-0.5 w-1.5 h-1.5 bg-teal-500 rounded-full" />
          </span>
          <span>Override (closed)</span>
        </div>
      </div>
    </div>
  );
}
